import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Icon } from '@polichat/flamboyant';
import InfoCard from '../../channels/CertificationWabaByPoli/InfoCard/InfoCard.component';
import { EmbeddedSignupFlow } from '../../channels/CertificationWabaByPoli/MetaSDK/EmbeddedSignupFlow';
import {
  Container,
  FourColumnsGrid,
  CardsContainer,
  TextInfos,
} from '../../channels/CertificationWabaByPoli/steps/FillForm/FillForm.styles';
import { useFacebookSdkService } from '../hooks/useFacebookSdkService';
import { useGlotaT } from '../../../../hooks/useGlotaTranslation';
import { Customer } from '../../channels/CertificationWabaByPoli/steps/FillForm/FillForm.component';

export interface Step0Props {
  onSubmit: () => void;
  customer: Customer | undefined;
}

export interface Step0Ref {
  submit: () => void;
  setDisplayErrorTrue: () => void;
  setDisplayErrorFalse: () => void;
}
export const Step0 = forwardRef<Step0Ref, Step0Props>((props, ref) => {
  const facebookSDK = useFacebookSdkService();
  
  // Traduções
  const nowYouEnterFacebookText = useGlotaT('spa.tools.migrateNowYouEnterFacebook');
  const theseAreTheStepsText = useGlotaT('spa.tools.migrateTheseAreTheSteps');
  const somethingWentWrongText = useGlotaT('spa.tools.migrateSomethingWentWrong');
  const deactivatePinOldProviderText = useGlotaT('spa.tools.migrateDeactivatePinOldProvider');
  const deactivatePinDescriptionText = useGlotaT('spa.tools.migrateDeactivatePinDescription');
  const loginText = useGlotaT('spa.tools.migrateLogin');
  const loginDescriptionText = useGlotaT('spa.tools.migrateLoginDescription');
  const chooseBusinessAccountText = useGlotaT('spa.tools.migrateChooseBusinessAccount');
  const chooseBusinessAccountDescriptionText = useGlotaT('spa.tools.migrateChooseBusinessAccountDescription');
  const wabaManagementAccountText = useGlotaT('spa.tools.migrateWabaManagementAccount');
  const wabaManagementAccountDescriptionText = useGlotaT('spa.tools.migrateWabaManagementAccountDescription');
  const closePopupText = useGlotaT('spa.tools.migrateClosePopup');
  const closePopupDescriptionText = useGlotaT('spa.tools.migrateClosePopupDescription');
  const tryAgainText = useGlotaT('spa.tools.tryAgain');
  const loadingText = useGlotaT('spa.tools.loading');

  const [isDisplayError, setIsDisplayError] = useState(false);

  useImperativeHandle(ref, () => ({
    submit: () => {
      props.onSubmit();
    },
    setDisplayErrorTrue: () => setIsDisplayError(true),
    setDisplayErrorFalse: () => setIsDisplayError(false),
  }));

  // ---------------------------------------------
  // Transformations
  useEffect(() => {
    /**
     * No proceso de migração só estamos interessados em ter acesso à conta WABA
     * desta forma quando for solicitado o telefone, precisa fechar o popup.
     * Quando fecha o popup, na criação retorna erro da callback do FacebookSDK.
     * Como na portabilidade, este é um processo esperado, assim que desparado o erro,
     * o usuário será liberado para o próximo passo da migração
     */
    if (facebookSDK.hasError || facebookSDK.data) {
      props.onSubmit();
    }
  }, [facebookSDK.hasError, facebookSDK.data]);
  // ---------------------------------------------
  // Render

  return (
    <div>
      <Container style={{ margin: '0.5rem', padding: '0' }}>
        <TextInfos>
          <span>
            {nowYouEnterFacebookText}
            <br />
            {theseAreTheStepsText}
          </span>
        </TextInfos>

        {isDisplayError && (
          <div>
            <h2>{somethingWentWrongText}</h2>
          </div>
        )}

        <CardsContainer style={{ margin: '0.5rem 0' }}>
          <InfoCard>
            <>
              <span style={{ fontWeight: 'bold' }}>
                {deactivatePinOldProviderText}
              </span>
              <p>
                {deactivatePinDescriptionText}
              </p>
            </>
          </InfoCard>
          <br />
          <FourColumnsGrid columns={'1fr 1fr 1fr 1fr'}>
            <InfoCard number="1">
              <>
                <div>
                  <span style={{ fontWeight: 'bold' }}>{loginText}</span>
                </div>

                <span className="mt-3">
                  {loginDescriptionText}
                </span>
              </>
            </InfoCard>

            <InfoCard number="2">
              <>
                <div>
                  <span style={{ fontWeight: 'bold' }}>
                    {chooseBusinessAccountText}
                  </span>
                </div>

                <span className="mt-3">
                  {chooseBusinessAccountDescriptionText}
                </span>
              </>
            </InfoCard>

            <InfoCard number="3">
              <>
                <div>
                  <span style={{ fontWeight: 'bold' }}>
                    {wabaManagementAccountText}
                  </span>
                </div>

                <span className="mt-3">
                  {wabaManagementAccountDescriptionText}
                </span>
              </>
            </InfoCard>

            <InfoCard number="4">
              <>
                <div>
                  <span style={{ fontWeight: 'bold', display: 'inline-flex' }}>
                    {closePopupText}
                    <Icon icon={'poli-icon pi-alert-line'} size={24} />
                  </span>
                </div>

                <span className="mt-3">
                  {closePopupDescriptionText}
                </span>
              </>
            </InfoCard>
          </FourColumnsGrid>
        </CardsContainer>

        {isDisplayError && (
          <p style={{ textAlign: 'center' }}>{tryAgainText}</p>
        )}
        {facebookSDK.loading && (
          <div style={{ textAlign: 'center' }}>
            <span>{loadingText}</span>
          </div>
        )}
        {/* Button Facebook SDK */}
        {facebookSDK.started && !facebookSDK.data && (
          <EmbeddedSignupFlow
            setErrorSdkFb={facebookSDK.handleError}
            onCallbackSDK={facebookSDK.handleData}
            customer={props.customer}
          />
        )}
      </Container>
    </div>
  );
});
